import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import AdminHeader from './AdminHeader';
import ExtensionPromptModal from './ExtensionPromptModal';
import QueueRolePromptModal from './QueueRolePromptModal';
import IncomingCallPop from './IncomingCallPop';
import { adminAPI, authAPI } from '../../services/api';
import {
  canAccessScreenPop,
  isExtensionConfirmedThisSession,
  setExtensionConfirmedThisSession,
  setScreenPopAccessFromLogin,
  setStoredPhoneExtension,
} from '../../utils/screenPopAccess';
import {
  clearQueueLoginRole,
  isQueueRoleConfirmedThisSession,
  setQueueLoginRole,
} from '../../utils/queueLoginRole';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_COLLAPSED_KEY = 'admin_sidebar_collapsed';

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'
  );
  const [needsExtension, setNeedsExtension] = useState(false);
  const [needsQueueRole, setNeedsQueueRole] = useState(false);
  const [extensionTick, setExtensionTick] = useState(0);
  const [initialExtension, setInitialExtension] = useState('');
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!sidebarOpen) {
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);

  useEffect(() => {
    let cancelled = false;

    const syncAccess = async () => {
      try {
        const { data } = await authAPI.me();
        if (cancelled) return;
        const me = data as {
          screen_pop_access?: boolean;
          phone_extension?: string | null;
        };
        setScreenPopAccessFromLogin(Boolean(me.screen_pop_access));
        if (me.phone_extension) {
          setStoredPhoneExtension(me.phone_extension);
          setInitialExtension(me.phone_extension);
        }

        const screenPop = Boolean(me.screen_pop_access);
        const extConfirmed = isExtensionConfirmedThisSession();

        if (screenPop && !extConfirmed) {
          setNeedsExtension(true);
          setNeedsQueueRole(false);
          return;
        }

        setNeedsExtension(false);

        // فقط اگر از مسیر لاگین جا مانده باشد (رفرش و غیره) — چک سریع از DB
        if (screenPop && extConfirmed && !isQueueRoleConfirmedThisSession()) {
          try {
            const { data: cfg } = await adminAPI.getQueueLoginConfig();
            if (cancelled) return;
            if (cfg.feature_enabled) {
              setNeedsQueueRole(true);
              return;
            }
            setQueueLoginRole('outside');
          } catch {
            // ignore
          }
        }

        if (!cancelled) setNeedsQueueRole(false);
      } catch {
        if (cancelled) return;
        if (canAccessScreenPop() && !isExtensionConfirmedThisSession()) {
          setNeedsExtension(true);
          setNeedsQueueRole(false);
        } else {
          setNeedsExtension(false);
          setNeedsQueueRole(false);
        }
      }
    };

    syncAccess();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, extensionTick]);

  return (
    <div className="flex min-h-screen bg-gray-50" style={{ direction: 'rtl' }}>
      <AdminSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
      />

      <div
        className={`flex h-screen min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden transition-[margin] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          sidebarCollapsed ? 'lg:mr-[4.5rem]' : 'lg:mr-64'
        }`}
      >
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} />

        <main className="min-w-0 flex-1 p-4 md:p-6 lg:p-8">{children}</main>
      </div>

      {needsExtension && (
        <ExtensionPromptModal
          initialExtension={initialExtension}
          onConfirmed={(ext) => {
            setStoredPhoneExtension(ext);
            setExtensionConfirmedThisSession(true);
            clearQueueLoginRole();
            setNeedsExtension(false);
            setExtensionTick((n) => n + 1);
          }}
        />
      )}

      {!needsExtension && needsQueueRole && (
        <QueueRolePromptModal
          onConfirmed={() => {
            setNeedsQueueRole(false);
            setExtensionTick((n) => n + 1);
          }}
        />
      )}

      <IncomingCallPop key={extensionTick} />
    </div>
  );
};

export default AdminLayout;
